import { CoiCheckDto, CoiCheckListItem, CoiCheckRelation, CoiCheckRelationDto, CoiCheckSummary } from 'types/coi-check';
import { objectToQueryParams } from 'utils/linkUtil';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APICoiChecksSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: ['CoiCheckView', 'CoiCheckSummary', 'CoiChecksCount', 'FindCoiCheckRelations'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCoiCheckSummaryById: builder.query<CoiCheckSummary, { id: string; isManuallySelected?: boolean }>({
                query: ({ id, isManuallySelected }) =>
                    `/v1/coi-checks/${id}${objectToQueryParams({ isManuallySelected })}`,
                keepUnusedDataFor: 0,
                providesTags: ['CoiCheckSummary'],
            }),
            getCoiChecksWithFilters: accumulatedQueryWithFilters<CoiCheckListItem>(builder, '/v1/coi-checks', {
                providesTags: ['CoiCheckView'],
            }),
            getCoiChecksCount: injectCountEndPoint(builder, '/v1/coi-checks', 'CoiChecksCount'),
            addCoiCheck: builder.mutation<CoiCheckSummary, CoiCheckDto>({
                query: ({ ...body }) => ({
                    url: `/v1/coi-checks`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['CoiCheckView', 'CoiCheckSummary', 'FindCoiCheckRelations'],
            }),
            addManuallySelectedCoiCheckRelation: builder.mutation<
                void,
                { entityId: string; relations: CoiCheckRelationDto[] }
            >({
                query: ({ ...body }) => ({
                    url: `/v1/coi-checks/${body.entityId}/relations`,
                    method: 'PUT',
                    body: body.relations,
                }),
                invalidatesTags: ['CoiCheckView', 'CoiCheckSummary', 'FindCoiCheckRelations'],
            }),
            findCoiCheckRelations: builder.query<
                CoiCheckRelation[],
                { entityId: string; scopeType: string; isManuallySelected?: boolean }
            >({
                query: ({ entityId, scopeType, isManuallySelected }) =>
                    `/v1/coi-checks/${entityId}/relations${objectToQueryParams({ scopeType, isManuallySelected })}`,
                providesTags: ['FindCoiCheckRelations'],
            }),
            deleteManuallySelectedCoiCheckRelation: builder.mutation<
                boolean,
                { coiCheckId: number; relationId: number }
            >({
                query: ({ coiCheckId, relationId }) => ({
                    url: `/v1/coi-checks/${coiCheckId}/relations/${relationId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['CoiCheckView', 'CoiCheckSummary', 'FindCoiCheckRelations'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetCoiCheckSummaryByIdQuery,
    useGetCoiChecksWithFiltersQuery,
    useLazyGetCoiChecksWithFiltersQuery,
    useFindCoiCheckRelationsQuery,
    useAddCoiCheckMutation,
    useAddManuallySelectedCoiCheckRelationMutation,
    useLazyGetCoiChecksCountQuery,
    useDeleteManuallySelectedCoiCheckRelationMutation,
} = APICoiChecksSlice;
